import { createFileRoute, Link } from "@tanstack/react-router"

import { Icon } from "@/components/icon/Icon"
import { LegalToc } from "@/components/legal/LegalToc"
import { Reveal } from "@/components/reveal/Reveal"
import { Seo } from "@/components/seo/Seo"
import {
	COMPANY_LEGAL_NAME,
	GRIEVANCE_EMAIL,
	GRIEVANCE_OFFICER_NAME,
	LAST_UPDATED_DATE,
	REGISTERED_OFFICE,
	SUPPORT_EMAIL,
} from "@/constants/company"

export const Route = createFileRoute("/privacy")({
	component: PrivacyPolicy,
})

const SECTIONS = [
	{ id: "section-1", label: "1. What We Collect" },
	{ id: "section-2", label: "2. Why We Hold It" },
	{ id: "section-3", label: "3. Who Can See It" },
	{ id: "section-4", label: "4. Your Documents" },
	{ id: "section-5", label: "5. How Long We Keep It" },
	{ id: "section-6", label: "6. What You Can Ask For" },
	{ id: "section-7", label: "7. Contact" },
]

function ArticleTag({ n, tag }: { n: string; tag: string }) {
	return (
		<div className="gap-space-xs flex items-center">
			<span className="bg-surface-container text-label-sm text-primary rounded px-2 py-0.5 font-sans font-semibold tracking-wider uppercase">
				Section {n}
			</span>
			<span className="text-label-sm text-on-surface-variant font-sans tracking-wider uppercase">
				• {tag}
			</span>
		</div>
	)
}

function Points({ items }: { items: string[] }) {
	return (
		<ul className="gap-space-xs pl-space-md flex flex-col">
			{items.map(item => (
				<li
					key={item}
					className="gap-space-xs text-body-md text-on-surface flex items-start font-sans"
				>
					<Icon
						name="task_alt"
						size={18}
						className="text-tertiary mt-1"
					/>
					<span>{item}</span>
				</li>
			))}
		</ul>
	)
}

function Section({
	n,
	tag,
	title,
	children,
}: {
	n: number
	tag: string
	title: string
	children: React.ReactNode
}) {
	return (
		<Reveal>
			<article
				id={`section-${n}`}
				className="gap-space-sm flex scroll-mt-28 flex-col"
			>
				<ArticleTag
					n={String(n).padStart(2, "0")}
					tag={tag}
				/>
				<h2 className="text-headline-lg text-on-surface font-serif">{title}</h2>
				{children}
			</article>
		</Reveal>
	)
}

const BODY = "text-body-md text-on-surface-variant font-sans"

function PrivacyPolicy() {
	return (
		<div className="flex flex-col">
			<Seo
				title="Privacy Notice"
				description="What Swamithra collects, why we hold it, who can see it, and what you can ask us to do with it."
				path="/privacy"
			/>
			<section className="bg-surface-container-low py-space-xl w-full">
				<div className="px-margin mx-auto max-w-7xl">
					<div className="gap-space-md flex flex-col justify-between lg:flex-row lg:items-end">
						<div>
							<span className="mb-space-xs bg-surface-container px-space-sm py-space-xs text-label-sm text-primary inline-block rounded font-sans font-semibold tracking-widest uppercase">
								Legal &amp; Regulatory Compliance
							</span>
							<h1 className="text-display-lg text-on-surface font-serif tracking-tight">
								Privacy Notice
							</h1>
						</div>
						<div className="gap-space-sm bg-surface-container-lowest px-space-md py-space-sm text-label-md text-on-surface-variant flex items-center rounded font-sans shadow-sm">
							<Icon
								name="verified_user"
								size={18}
								filled
								className="text-tertiary"
							/>
							<span>
								Last updated: <strong>{LAST_UPDATED_DATE}</strong>
							</span>
						</div>
					</div>
				</div>
			</section>

			<section className="py-space-xl w-full">
				<div className="px-margin mx-auto max-w-7xl">
					<div className="gap-space-xl grid grid-cols-1 lg:grid-cols-12">
						<aside className="lg:col-span-4">
							<div className="gap-space-md sticky top-28 flex flex-col">
								<LegalToc
									sections={SECTIONS}
									title="On This Page"
								/>
								<div className="gap-space-sm bg-surface-container p-space-md flex flex-col rounded-xl shadow-sm">
									<div className="gap-space-xs text-label-md text-primary flex items-center font-sans font-semibold">
										<Icon
											name="verified"
											size={18}
										/>
										<span>Data Fiduciary</span>
									</div>
									<p className="text-body-md text-on-surface-variant font-sans">
										{COMPANY_LEGAL_NAME} is the data fiduciary for the personal
										data you provide, under the Digital Personal Data Protection
										Act, 2023.
									</p>
								</div>
								<div className="bg-surface-container-high p-space-md rounded-xl shadow-sm">
									<div className="mb-space-xs gap-space-sm flex items-center">
										<Icon
											name="policy"
											size={20}
											className="text-primary"
										/>
										<h4 className="text-headline-sm text-on-surface font-serif">
											Grievance Officer
										</h4>
									</div>
									<p className="text-body-md text-on-surface-variant mb-space-sm font-sans">
										{GRIEVANCE_OFFICER_NAME}
									</p>
									<a
										href={`mailto:${GRIEVANCE_EMAIL}`}
										className="text-label-sm text-primary inline-flex items-center gap-1 font-sans font-semibold hover:underline"
									>
										<span>{GRIEVANCE_EMAIL}</span>
										<Icon
											name="mail"
											size={14}
										/>
									</a>
								</div>
								<div className="bg-surface-container-high p-space-md rounded-xl shadow-sm">
									<div className="mb-space-xs gap-space-sm flex items-center">
										<Icon
											name="delete_forever"
											size={20}
											className="text-primary"
										/>
										<h4 className="text-headline-sm text-on-surface font-serif">
											Want your data deleted?
										</h4>
									</div>
									<p className="mb-space-sm text-body-md text-on-surface-variant font-sans">
										See how to request account and data deletion.
									</p>
									<Link
										to="/data-deletion"
										className="text-label-sm text-primary group inline-flex items-center gap-1 font-sans font-semibold hover:underline"
									>
										<span>Data &amp; Account Deletion</span>
										<Icon
											name="arrow_forward"
											size={14}
											className="transition-transform duration-300 ease-out group-hover:translate-x-1"
										/>
									</Link>
								</div>
							</div>
						</aside>

						<div className="gap-space-xl pb-space-xl flex max-w-[68ch] flex-col lg:col-span-8">
							<Reveal className="bg-surface-container-lowest p-space-lg rounded-xl leading-relaxed shadow-sm">
								<p className={BODY}>
									What we collect, why we hold it, and what you can ask us to do
									with it.
								</p>
							</Reveal>

							<Section
								n={1}
								tag="Collection"
								title="What We Collect"
							>
								<Points
									items={[
										"Your WhatsApp number, which is how you sign in.",
										"Your name, and your email address if you give one.",
										"The documents you upload and the questions you write.",
										"Basic technical records of requests to the service, kept to keep it running and secure.",
									]}
								/>
							</Section>

							<Section
								n={2}
								tag="Purpose"
								title="Why We Hold It"
							>
								<Points
									items={[
										"To sign you in and keep your session open.",
										"To let an expert read your Queries/Drafts/Documents and answer it.",
										"To send you OTPs and notifications on WhatsApp.",
										"To keep your questions and documents together so you can find them later.",
									]}
								/>
								<p className={BODY}>
									We do not sell your information, and we do not use your
									documents to advertise to you.
								</p>
							</Section>

							<Section
								n={3}
								tag="Access"
								title="Who Can See It"
							>
								<Points
									items={[
										"You can.",
										"The experts on our panel who work on your question/vetting request.",
										"WhatsApp, which carries the OTPs and notifications we send to your number.",
										"Service providers who host the system, under contract and only to run it.",
									]}
								/>
							</Section>

							<Section
								n={4}
								tag="Documents"
								title="Your Documents"
							>
								<p className={BODY}>
									Links to a document are short-lived and generated fresh each
									time it is opened, so a link that is copied or forwarded stops
									working.
								</p>
							</Section>

							<Section
								n={5}
								tag="Retention"
								title="How Long We Keep It"
							>
								<p className={BODY}>
									We keep your account and your threads while your account is
									open, and for a period afterwards where we are required to. That
									period is being confirmed and will be stated here.
								</p>
							</Section>

							<Section
								n={6}
								tag="Your Rights"
								title="What You Can Ask For"
							>
								<Points
									items={[
										"Deletion of your account and its contents, where we are not required to keep it.",
									]}
								/>
								<p className={BODY}>
									Write to{" "}
									<a
										href={`mailto:${SUPPORT_EMAIL}`}
										className="text-primary hover:underline"
									>
										{SUPPORT_EMAIL}
									</a>{" "}
									and we will handle it.
								</p>
							</Section>

							<Section
								n={7}
								tag="Contact"
								title="Contact"
							>
								<p className={BODY}>
									Swamithra Tax KPO Private Limited, {REGISTERED_OFFICE}. Privacy
									questions and complaints go to{" "}
									<a
										href={`mailto:${SUPPORT_EMAIL}`}
										className="text-primary hover:underline"
									>
										{SUPPORT_EMAIL}
									</a>
									.
								</p>
							</Section>
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}
